<template>
  <div id="cash-closure-editor" :class="{ active: isActive }">
    <div class="header">
      <h3>{{ $t('CLOSURE') }}</h3>
      <div class="close" @click="() => hide()">
        <div class="line line-1"></div>
        <div class="line line-2"></div>
      </div>
    </div>

    <div class="body">
      <toggle-box ref="closureDateBox">
        <template v-slot:control>
          <text-input
            v-model="closureDate"
            @focus="() => showCalendar(true)"
            ref="closureDateInput"
            :label="$t('DATE')"
            dynamic-label
            required
            class="closure-date-input"
          >
            <template v-slot:icon>
              <font-awesome-icon class="icon" :icon="faCalendarAlt" />
            </template>
          </text-input>
        </template>
        <template v-slot:content>
          <calendar
            :date="closureDate || undefined"
            @dateChange="setclosureDate"
          />
        </template>
      </toggle-box>

      <bamboo-input
        :items="funds"
        :label="$t('FUND')"
        v-model="fund"
        required
        ref="fundInput"
      />

      <text-input
        v-model="balance"
        ref="balanceInput"
        :label="$t('BALANCE')"
        dynamic-label
        :format="addCommas"
        :hint="changeDigitToViText(balance, false) || $t('LEAVE_EMPTY_TO_CALCULATE_AUTOMATICALLY')"
        class="amount-input"
      />

      <text-input
        v-model="notes"
        ref="notesInput"
        :label="$t('NOTES')"
        dynamic-label
      />

      <primary-button rounded-corner :text="$t('OK')" blue @click="() => submit()" />
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

import { DATE_FORMAT, ERROR_CODES } from '@/consts';
import { addCommas, removeCommas, changeDigitToViText } from '@/helpers';

import TextInput from '@/components/TextInput';
import Calendar from '@/components/Calendar';
import ToggleBox from '@/components/ToggleBox';
import PrimaryButton from '@/components/PrimaryButton';
import BambooInput from '@/components/BambooInput';

import cashLogService from '@/views/CashLog/CashLogService';

const { useState: useAppState } = createNamespacedHelpers('app');
const { useActions: useFlashActions } = createNamespacedHelpers('flash');

export default {
  components: {
    TextInput,
    Calendar,
    ToggleBox,
    FontAwesomeIcon,
    PrimaryButton,
    BambooInput
  },
  props: {
    active: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const { funds: funds_ } = useAppState(['funds']);
    const { error } = useFlashActions(['error']);

    const funds = computed(() => funds_.value.map((b) => ({
      value: b.id,
      text: b.name
    })));

    const isActive = computed(() => props.active);

    const closureDate = ref(moment().format(DATE_FORMAT));
    const closureDateBox = ref(null);
    const closureDateInput = ref(null);

    const fund = ref(null);
    const fundInput = ref(null);

    const balance = ref(null);

    const notes = ref('');

    const hide = () => {
      emit('close');
    };

    const setclosureDate = (val) => {
      closureDate.value = val;
      closureDateBox.value.toggle(false);
    };

    const showCalendar = () => {
      closureDateBox.value.toggle(true);
      closureDateInput.value.blur();
    };

    const submit = async () => {
      const inputs = [
        closureDateInput.value,
        fundInput.value,
      ];

      let valid = true;

      inputs.forEach((input) => {
        const inputValidity = input.validateInput();
        valid = valid && inputValidity;
      });

      if (valid) {
        const data = {
          closureDate: closureDate.value,
          fund: fund.value,
          balance: balance.value ? removeCommas(balance.value) : undefined,
          notes: notes.value || undefined,
        };

        try {
          await cashLogService.createCashClosure(data);
          emit('submitted');
        } catch (err) {
          const { code, message } = err;
          const cashLogErrorCodes = [
            ERROR_CODES.CASH_LOG_PENDING_LOGS_IN_CLOSURE_RANGE,
            ERROR_CODES.CASH_LOG_CLOSURE_EXISTS,
            ERROR_CODES.CASH_LOG_CANNOT_BE_A_FUTURE_DATE,
          ];
          const i = cashLogErrorCodes.findIndex((c) => c === code);

          if (i === 0) {
            error('CASH_LOG_PENDING_LOGS_IN_CLOSURE_RANGE');
          } else if (i === 1) {
            error('CASH_LOG_CLOSURE_EXISTS');
          } else if (i === 2) {
            error('CASH_LOG_CANNOT_BE_A_FUTURE_DATE');
          } else {
            error(message);
          }
        }
      }
    };

    watch(isActive, (val) => {
      if (val) {
        closureDate.value = moment().format(DATE_FORMAT);
        fund.value = null;
        balance.value = null;
        notes.value = '';
      }
    });

    return {
      isActive,
      hide,
      faCalendarAlt,
      closureDate,
      closureDateBox,
      closureDateInput,
      setclosureDate,
      showCalendar,
      addCommas,
      changeDigitToViText,
      funds,
      fund,
      fundInput,
      balance,
      notes,
      submit
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#cash-closure-editor {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  @media (min-width: 992px) {
    width: 400px;
  }
  border-left: 1px solid $alto;
  background-color: $babyPowder;
  padding: 15px;
  z-index: 999;
  box-sizing: border-box;
  transform: translateX(100%);
  transition: all .2s ease-in-out;
  overflow-y: auto;

  &.active {
    transform: none;
  }

  .header {
    background-color: $blueNcs;
    padding: 11px 15px;
    padding-bottom: 5px;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;

    h3 {
      font-size: 18px;
      color: $babyPowder;
    }

    .close {
      margin-left: 15px;
      display: inline-block;
      position: absolute;
      top: 10px;
      right: 15px;
      width: 20px;
      height: 20px;
      cursor: pointer;
      opacity: .5;
      transition: all .2s ease-in-out;

      .line {
          height: 1px;
          width: 20px;
          background-color: $babyPowder;
          transform-origin: center;
          margin: 0 auto;
      }

      .line-1 {
          transform: translateY(11px) rotate(45deg);
      }

      .line-2 {
          transform: translateY(10px) rotate(135deg);
      }

      &:hover {
        opacity: 1;
      }
    }
  }

  .body {
    margin-top: 50px;

    > .toggle-box {
      margin-bottom: 20px;

      > input, > .display, > .input > input {
        border-radius: 4px;
      }
    }

    > .input {
      margin-bottom: 20px;
      width: 100%;

      > input, > .display, > .input > input {
        border-radius: 7px;
      }
    }

    .closure-date-input {
      width: 140px;
    }

    .amount-input {
      input {
        width: 50%;
        display: block;
      }
    }

    .primary-button {
      width: 100%;
    }
  }
}
</style>
